//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { FaqSection } from '../../components/FaqSection'
import { COLOR_SOLUTIONS, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    enteringUKHighlights,
    enteringUKChallenges,
    enteringUKSolutionStack,
    enteringUKSteps,
    enteringUKOutcomes,
    enteringUKQualifiers,
    enteringUKFaqs,
    enteringUKRelated,
} from '../../common/SolutionGrowFasterUKPublicSectorContent'

const C = COLOR_SOLUTIONS

export default function EnteringUKPublicSector() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Grow Faster in UK Public Sector"
                description="Already winning public sector work and want to grow faster? CFP Core Pro, Tender Ninja and expert Win Reviews give you the tools to find more opportunities, bid smarter and increase your win rate."
                canonical="/solutions/grow-faster-uk-public-sector"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Getting Started · Established Supplier" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Win More Tenders in the{' '}
                                <Box component="span" sx={{ color: C }}>UK Public Sector</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                You're already selling to government. Now it's time to do it better, more tenders, stronger bids and higher win rates
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {enteringUKHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three things holding your win rate back"
                subtitle="These are the most common blockers for established suppliers. Each one is addressable with the right tools and process"
                items={enteringUKChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Three components that work together, a platform for complete market coverage, AI-assisted bid writing and an expert review of your BD strategy. Together they transform your win rate"
                items={enteringUKSolutionStack}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="Your route to a higher win rate"
                subtitle="Four steps from fragmented monitoring to a structured, measurable BD process that scales"
                steps={enteringUKSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After setting up CFP Core Professional, deploying Tender Ninja and completing a Win Review, this is the position you'll be in"
                items={enteringUKOutcomes}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={enteringUKQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="The platform grows with you. Here's where most established suppliers head next"
                items={enteringUKRelated}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                subtitle="From established suppliers who've been exactly where you are now"
                items={enteringUKFaqs}
                color={DARK_800}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to win more government contracts?"
                subtitle="Book a no-obligation discovery call. We'll confirm the right starting point for your business"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
